import { ReviewPartSchemaDetailsCourierTypeNameEnum, SubmissionStatusEnum } from 'bundles/grading/api/types';
import type {
  AssignmentRubric,
  AutoGradedQuestion,
  GradingStatusCounts,
  GradingStatusSummary,
  ManuallyGradedQuestion,
  QuestionRubric,
  QuestionRubricElement,
  QuestionRubricPrompt,
  QuestionRubricResponseContainer,
  SubmissionQuestionBase,
  SubmitterListTimestamp,
} from 'bundles/grading/api/types';
import type {
  Learner,
  SGASubmitter,
  Team,
} from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { isTeamSubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { getRubricPrompt } from 'bundles/grading/utils/formPartUtils';

/**
 * Checks if a submitter has a submission or not
 *
 * @param { SubmitterListTimestamp | null | undefined } submittedAt
 * @returns boolean
 */
export const hasSubmission = (submittedAt?: SubmitterListTimestamp | null): boolean => {
  return submittedAt !== null && submittedAt !== undefined;
};

/**
 * Returns whether a submitter's submission is graded or not
 * based on the given status of the submitter
 *
 * @param { SubmissionStatusEnum } status
 * @returns boolean
 */
export const isGraded = (status?: SubmissionStatusEnum | null): boolean => {
  return (
    status !== undefined &&
    status !== null &&
    ([SubmissionStatusEnum.GradeReleased, SubmissionStatusEnum.GradingFinished] as SubmissionStatusEnum[]).includes(
      status
    )
  );
};

/**
 * Returns true when grades were submitted previously, but Grader updated rubrics without re-submitting to the gradebook.
 * @param submissionStatus
 * @param isSubmissionGraded
 * @returns
 */
export const hasUnsubmittedChanges = (
  submissionStatus?: SubmissionStatusEnum | null,
  isSubmissionGraded?: boolean | null
): boolean => {
  return (
    submissionStatus !== undefined &&
    isSubmissionGraded !== undefined &&
    isSubmissionGraded !== null &&
    submissionStatus === SubmissionStatusEnum.GradingStarted &&
    isSubmissionGraded
  );
};

/**
 * Returns whether a submitter has a submission or not based on their
 * status. This should only be used when necessary, opt to use hasSubmission
 * using submittedAt timestamp instead.
 *
 * Edge case: Assignments containing only "off platform" question are available
 * for grading even though `submittedAt=null`. Such submissions skip
 * `NOT_SUBMITTED` status, and become `READY_FOR_GRADING` without learner's
 * input.
 *
 * @param { SubmissionStatusEnum } status
 * @returns boolean
 */
export const hasSubmissionFromStatus = (status: SubmissionStatusEnum): boolean => {
  return (
    [
      SubmissionStatusEnum.AwaitingPeerReview,
      SubmissionStatusEnum.GradeReleased,
      SubmissionStatusEnum.GradingFinished,
      SubmissionStatusEnum.GradingStarted,
      SubmissionStatusEnum.ReadyForGrading,
    ] as SubmissionStatusEnum[]
  ).includes(status);
};

/**
 * Returns the total number of submitters considered "graded"
 * from the status counts list
 *
 * @param { GradingStatusCounts[] } statusCounts
 * @returns number
 */
export const getGradedTotal = (statusCounts: GradingStatusCounts[]): number => {
  const gradedStatuses = statusCounts.filter((statusCount) => isGraded(statusCount.status));
  return gradedStatuses.reduce((sum, nextStatus) => sum + nextStatus.count, 0);
};

/**
 * Updates a given gradingStatusSummary object with a status change
 * given the current and new statuses. Will recompute totals if needed
 *
 * @param { GradingStatusSummary } gradingStatusSummary
 * @param { SubmissionStatusEnum } currentStatus
 * @param { SubmissionStatusEnum } newStatus
 * @returns GradingStatusSummary
 */
export const updateGradingStatusSummaryForSubmission = (
  gradingStatusSummary: GradingStatusSummary,
  currentStatus: SubmissionStatusEnum,
  newStatus: SubmissionStatusEnum
): GradingStatusSummary => {
  const newGradingStatusSummary = { ...gradingStatusSummary };
  const currentStatusIndex = newGradingStatusSummary.statusCounts.findIndex(
    (statusCount) => statusCount.status === currentStatus
  );

  // current status index should exist in summary object, but as a fail safe decrement the count associated only if found
  if (currentStatusIndex !== -1) {
    newGradingStatusSummary.statusCounts[currentStatusIndex].count -= 1;

    // if this status index reached 0, then remove it from the array to ensure the behavior is consistent with BE responses
    // where statuses with count=0 are never given
    if (newGradingStatusSummary.statusCounts[currentStatusIndex].count === 0) {
      newGradingStatusSummary.statusCounts.splice(currentStatusIndex, 1);
    }
  }

  // grab new status index after processing current status in case the status was removed from the array and we get updated index
  const newStatusIndex = newGradingStatusSummary.statusCounts.findIndex(
    (statusCount) => statusCount.status === newStatus
  );

  // increment new status count if it exists
  if (newStatusIndex !== -1) {
    newGradingStatusSummary.statusCounts[newStatusIndex].count += 1;
  }
  // otherwise add this new status count to the array
  else {
    newGradingStatusSummary.statusCounts.push({
      count: 1,
      status: newStatus,
      __typename: 'GroupGrading_GradingStatusCount',
    });
  }

  // recompute totals with added/removed elements if needed
  newGradingStatusSummary.total = newGradingStatusSummary.statusCounts.reduce(
    (sum, nextStatus) => sum + nextStatus.count,
    0
  );

  return newGradingStatusSummary;
};

/**
 * Returns a learner's email or team's emails as a single string
 * comma separated. For anonymous submitter or unavailable email
 * it will return an empty string
 * @param { SGASubmitter } currentSubmitter
 * @returns string
 */
export const getSubmitterEmail = (currentSubmitter: SGASubmitter): string => {
  return isTeamSubmitter(currentSubmitter)
    ? (currentSubmitter as Team).userProfiles
        .map((user) => user.email)
        .filter((email) => email)
        .join(',')
    : (currentSubmitter as Learner).email || '';
};

/**
 * Returns userId of the passed {Learner}. Or the userId of the first user from the passed {Team}.
 * @param { SGASubmitter } currentSubmitter
 * @return number
 */
export const getSubmitterUserId = (currentSubmitter: SGASubmitter): number => {
  const userSubmitterId = isTeamSubmitter(currentSubmitter)
    ? (currentSubmitter as Team).userProfiles[0].userId
    : (currentSubmitter as Learner).userId;
  return Number(userSubmitterId);
};

/**
 * Returns true when a question is manual-graded
 * @param {SubmissionQuestionBase} question
 * @returns boolean
 */
export const isManuallyGradedQuestion = (question: SubmissionQuestionBase): question is ManuallyGradedQuestion => {
  return question.__typename === 'GroupGrading_QuestionManuallyGraded';
};

/**
 * Returns true when a question is auto-graded
 * @param {SubmissionQuestionBase} question
 * @returns boolean
 */
export const isAutoGradedQuestion = (question: SubmissionQuestionBase): question is AutoGradedQuestion => {
  return question.__typename === 'GroupGrading_QuestionAutoGraded';
};

/**
 * Returns true when a rubric is inline-feedback rubric
 * @param {AssignmentRubric | QuestionRubric} rubric
 * @returns boolean
 */
export const isRubricBoxView = (rubric: AssignmentRubric | QuestionRubric): boolean => {
  const currentRubricPrompt = getRubricPrompt(rubric);

  return (
    currentRubricPrompt?.reviewPartSchemaDetails.courierTypeName ===
    ReviewPartSchemaDetailsCourierTypeNameEnum.BoxViewDocumentAnnotationReviewSchema
  );
};

/**
 * Returns true when a rubric is a textbox rubric
 * @param {AssignmentRubric | QuestionRubric} rubric
 * @returns boolean
 */
export const isRubricTextBox = (rubric: AssignmentRubric | QuestionRubric): boolean => {
  const currentRubricPrompt = (rubric?.formElementNode?.element as QuestionRubricElement)
    ?.prompt as QuestionRubricPrompt;

  return (
    currentRubricPrompt?.reviewPartSchemaDetails.courierTypeName ===
    ReviewPartSchemaDetailsCourierTypeNameEnum.MultiLineInputReviewSchema
  );
};

export const isAiGradedRubric = (rubric: AssignmentRubric | QuestionRubric): boolean => {
  return (
    ((rubric?.formElementNode?.element as QuestionRubricElement).response as QuestionRubricResponseContainer)
      .graderType === 'AI'
  );
};
